import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Slide,
  useScrollTrigger,
  Avatar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/material/styles";

// Custom styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)",
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: "white",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "1rem",
  borderRadius: "8px",
  padding: "8px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  letterSpacing: "1px",
  background: "linear-gradient(to right, #fff, #a1c4fd)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Poltics", path: "/politics-news" },
    { name: "Business", path: "/business-news" },
    { name: "Sport", path: "/sports-news"},
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <HideOnScroll>
      <StyledAppBar position="fixed" elevation={scrolled ? 4 : 1}>
        <Toolbar
          sx={{
            py: scrolled ? 0.5 : 1,
            transition: "all 0.3s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            <Avatar
              src="/logo.png"
              alt="EthioReview Logo"
              sx={{
                width: 40,
                height: 40,
                mr: 1,
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}
            />
            <LogoText
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ textDecoration: "none" }}
            >
              EthioReview
            </LogoText>
          </Box>

          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="mobile-menu"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: "12px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    "& .MuiMenuItem-root": {
                      "&:hover": {
                        backgroundColor: "rgba(30, 60, 114, 0.1)",
                      },
                    },
                  },
                }}
              >
                {navItems.map((item) => (
                  <MenuItem
                    key={item.name}
                    onClick={handleMenuClose}
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      py: 1.5,
                      "&:hover": {
                        transform: "translateX(5px)",
                        transition: "transform 0.2s ease",
                      },
                    }}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {navItems.map((item) => (
                <NavButton
                  key={item.name}
                  component={RouterLink}
                  to={item.path}
                >
                  {item.name}
                </NavButton>
              ))}
            </Box>
          )}
        </Toolbar>
      </StyledAppBar>
    </HideOnScroll>
  );
};

export default Navbar;
